import * as React from 'react';

import {
  DrawerContext,
  FilterChip,
  IconRegularClose,
} from '@notino/react-styleguide';
import { GetReviewsQuery } from '@notino/shared/definitions/types';

import { useTrackingContext } from '@context/tracking/TrackingContext';

import { Footer } from '../components/Footer';
import { DrawerTitle } from '../components/Title';
import { FILTER_DRAWER_TITLE_ID } from '../constants';
import {
  useReviewFilterActions,
  ReviewFilterCategory,
  useReviewFiltersStore,
} from '../Stores/filtersStore';
import { DrawerWrapper } from '../styled';

import { buildFilterChips } from './chips';
import { ChipTitle } from './components/ChipTitle';
import { normalizeCategory } from './helpers';
import { FiltersWrapper } from './styled';
import { trackFiltration } from './tracking';

interface FiltersProps {
  filters: NonNullable<GetReviewsQuery['reviewsAndFilters']>['filter'];
}

export const Filters: React.FC<FiltersProps> = ({ filters }) => {
  const { openDrawer } = DrawerContext.useDrawerContext();
  const { setFilters, openCategory, clearCategory } = useReviewFilterActions();

  const { getTimeFromInit } = useTrackingContext();

  const allSelections = useReviewFiltersStore((state) => state.selections);

  React.useEffect(() => {
    setFilters(filters);
  }, [filters, setFilters]);

  if (!filters) {
    return null;
  }

  const visibleChips = buildFilterChips(filters).filter((c) => c.show);

  const onChipClick = (
    category: ReviewFilterCategory,
    content: React.ReactElement,
    title: React.ReactElement
  ) => {
    openCategory(category);

    openDrawer(
      <DrawerWrapper role="group" aria-labelledby={FILTER_DRAWER_TITLE_ID}>
        {content}
      </DrawerWrapper>,
      {
        footerContent: <Footer />,
        title: <DrawerTitle id={FILTER_DRAWER_TITLE_ID} name={title} />,
        initialPosition: 'dynamic',
        isTitleBorderHidden: true,
        hideScrollbar: true,
        showCloseIcon: false,
      }
    );
    trackFiltration({
      interaction: 'click_drawer_open',
      timing: getTimeFromInit(),
      name: category,
      action: 'filtration_drawer_opened',
    });
  };

  const onCloseClick = (category: ReviewFilterCategory) => {
    const normalizedCategory = normalizeCategory(category);

    clearCategory(category);
    trackFiltration({
      interaction: 'click_remove',
      timing: getTimeFromInit(),
      name: category,
      action: 'filtration_removed',
      type: normalizedCategory,
    });
  };

  return (
    <FiltersWrapper>
      {visibleChips.map(({ key, title, content }) => (
        <FilterChip
          key={key}
          isActive={allSelections[key].length}
          title={
            <ChipTitle
              category={key as ReviewFilterCategory}
              defaultTitle={title}
            />
          }
          secondaryIcon={<IconRegularClose width="16px" height="16px" />}
          onSecondaryIconClick={
            allSelections[key].length > 0
              ? () => onCloseClick(key as ReviewFilterCategory)
              : undefined
          }
          onClick={() => {
            onChipClick(key as ReviewFilterCategory, content, title);
          }}
        />
      ))}
    </FiltersWrapper>
  );
};
